import { v4 as uuidv4 } from 'uuid'
import Dexie, { EntityTable } from 'dexie'
import { isWindowDefined } from '@chegg/utils'
import { JSONContent } from '@tiptap/core'
import { Feedback } from 'core/types/paraphraser'
import { SentenceOffset } from 'core/state/paraphraser/types'

export const INDEXED_DB_NAME = 'ParaphraserDatabase'

export type PaperDBType = {
  id: string
  title: string
  created_at: string
  paperContent: {
    editorJSON: JSONContent
  }
  feedbackState: {
    feedbackList: Feedback[]
    sentences: Omit<SentenceOffset, 'originalText' | 'lastUpdated'>[]
    totalFeedbackCount: number
  }
  requiresRecheck: boolean
  updated_at?: string
}

interface ParaphraseDbType extends Dexie {
  papers: EntityTable<
    PaperDBType,
    'id' // primary key "id" (for the typings only)
  >
}

const getIndexedDb = () => {
  let paperDb: ParaphraseDbType
  return () => {
    if (isWindowDefined) {
      if (!paperDb) {
        paperDb = new Dexie(INDEXED_DB_NAME) as ParaphraseDbType
        // Define the structure of your database
        paperDb.version(1).stores({
          papers: '&id, title, created_at', // primary key "id" (for the runtime!)
        })
      }
      return paperDb
    }
    return null
  }
}

const getPaperIndexedDb = getIndexedDb()

export default getPaperIndexedDb

/**
 * ------------------------------------------------------
 * Utility functions to create a paper
 * ------------------------------------------------------
 */
export const createPaper = async (
  title: string,
  paperContent: PaperDBType['paperContent'],
  feedbackState: PaperDBType['feedbackState']
) => {
  const paperId = uuidv4()
  const createdAt = new Date().toISOString()
  try {
    const paraphraserIndexDb = getPaperIndexedDb()
    const id = await paraphraserIndexDb.papers.add({
      id: paperId,
      title,
      created_at: createdAt,
      paperContent,
      feedbackState,
      requiresRecheck: false,
    })
    console.log(`Paper created with id ${id}`)
    return id
  } catch (error) {
    console.log(`Paper creation failed`)
  }
}

/**
 * ----------------------------------------------------
 * Utility functions to read a paper in local storage
 * ----------------------------------------------------
 */
export const readPaper = async (paperId): Promise<PaperDBType> => {
  try {
    const paraphraserIndexDb = getPaperIndexedDb()
    const paper = await paraphraserIndexDb.papers.get(paperId)
    console.log(`Paper read for id ${paperId}`, paper)
    if (!paper) {
      throw new Error(`Couldn't found Paper ${paperId}.`)
    }
    return paper
  } catch (error) {
    console.log(`Paper read failed for id ${paperId}`)
  }
}

/**
 * ----------------------------------------------------
 * Utility functions to update a paper in local storage
 * ----------------------------------------------------
 */
export const updatePaper = async (
  paperId: string,
  data: {
    title?: string
    paperContent?: PaperDBType['paperContent']
    feedbackState?: PaperDBType['feedbackState']
    requiresRecheck?: boolean
  }
) => {
  try {
    const paraphraserIndexDb = getPaperIndexedDb()
    const updatedAt = new Date().toISOString()
    const { title, paperContent, feedbackState, requiresRecheck } = data
    const isUpdated = await paraphraserIndexDb.papers.update(paperId, {
      ...(title && { title }),
      ...(paperContent && { paperContent }),
      ...(feedbackState && { feedbackState }),
      ...(requiresRecheck != null && { requiresRecheck }),
      updated_at: updatedAt,
    })

    if (!isUpdated) {
      throw new Error(
        `Couldn't found Paper ${paperId}. Use "createPaper()" instead to create a paper.`
      )
    }
  } catch (error) {
    console.log(`Paper update failed for id ${paperId}`)
  }
}

/**
 * ----------------------------------------------------
 * Utility functions to delete a paper from local storage
 * ----------------------------------------------------
 */
export const deletePaper = (paperId) => {
  try {
    const paraphraserIndexDb = getPaperIndexedDb()
    paraphraserIndexDb.papers.delete(paperId)
  } catch (error) {
    console.log(`Paper update failed for id ${paperId}`)
  }
}

/**
 * Returns First record from papers table in IndexDB,
 * @returns First Paper in IndexDB, if exists, else null
 */
export const getFirstPaper = async (): Promise<PaperDBType> => {
  const paraphraserIndexDb = getPaperIndexedDb()
  const papers = await paraphraserIndexDb.papers.limit(1).toArray()
  if (papers && papers.length > 0) {
    return papers[0]
  }
  return null
}
